import { ApolloProvider } from '@apollo/react-hooks';
import { ThemeProvider as BowkitThemeProvider } from '@emotion/react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { BrowserRouter as Router } from 'react-router-dom';
import { produce } from 'immer';
import { IframeBaselineStyles } from './BaseLineStyles';
import QuotationWidget from './QuotationWidget';
import Header from './QuotationWidget/Header';
import client from './client';
import { BASENAME } from './constants';
import {
  AttributeAndQsContextProps,
  AttributeAndQsContextProvider,
} from './contexts/AttributeAndQsContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { QuotationProvider } from './contexts/QuotationContext';
import { UserAuthProvider } from './contexts/UserAuthContext';
import theme from './helpers/theme';
import {
  AuthProvider,
  createStatsigClient,
  CustomIds,
  ENVIRONMENT,
  ExperimentProvider,
  ExperimentProviderProps,
  retrieveTrackingAlias,
  UnexpectedError,
  USER_CHANGE_CALLBACK,
  UserChangeCallback,
} from '@bowtie-web/common';
import { BowtieDialogSpinner } from '@bowtie-web/components';
import Notifier from './Notifier';
import getLogger from './logging';

type AppProps = {
  attributeValues?: AttributeAndQsContextProps;
};

const logger = getLogger('experiment');

// This fail-open path can fire for a large share of users (anyone whose
// tracking-alias lookup fails), so capturing every occurrence floods Sentry.
// Surface only a sample as an exception; every hit still leaves a breadcrumb,
// so the failure stays visible in the trail of any captured event.
// Kept low (1/200) to keep this high-volume issue to a small fraction of the
// Sentry quota; pair with a server-side per-issue rate limit for spikes.
const EXPERIMENT_LOG_SAMPLE_RATE = 0.005;

const onUserChange: UserChangeCallback = (user, prevUser) => {
  USER_CHANGE_CALLBACK.LOGGER(user, prevUser);
  USER_CHANGE_CALLBACK.TRACKING(user, prevUser);
};

const statsigClientSdkKey =
  import.meta.env.REACT_APP_STATSIG_CLIENT_SDK_KEY || '';

const statsigClient = createStatsigClient({
  clientSdkKey: statsigClientSdkKey,
  environment: ENVIRONMENT,
});

const initializeExperimentUser: ExperimentProviderProps['initializeUser'] =
  async user => {
    try {
      const { id } = await retrieveTrackingAlias();
      const updated = produce(user, draft => {
        draft.customIDs = { [CustomIds.AliasId]: id };
      });
      return updated;
    } catch (err) {
      // Fail open: proceed without the alias id so the widget still loads.
      // The user falls back to default flag values (the experiment is not
      // applied). TODO: once an experiment graduates, retire its gate so these
      // users get the launched screen rather than the default.
      const error = err instanceof Error ? err : new Error(String(err));
      // Breadcrumb on every occurrence; sample only the captured exception so
      // a broadly-failing endpoint can't flood Sentry.
      logger.log('Fail to retrieve tracking alias', error.message);
      if (Math.random() < EXPERIMENT_LOG_SAMPLE_RATE) {
        logger.raise(
          new UnexpectedError('Fail to retrieve tracking alias', error),
        );
      }
      return user;
    }
  };

export const ThemeProvider: FC = ({ children }) => {
  const { locale: lang } = useIntl();

  const locale = useMemo(() => (lang.includes('zh') ? 'zh' : 'en'), [lang]);

  return (
    <MuiThemeProvider theme={theme}>
      <BowkitThemeProvider theme={{ ...theme, locale }}>
        <IframeBaselineStyles />
        {children}
      </BowkitThemeProvider>
    </MuiThemeProvider>
  );
};

const App = ({ attributeValues }: AppProps) => (
  <Router basename={BASENAME}>
    <AttributeAndQsContextProvider attributeValues={attributeValues}>
      <LocaleProvider>
        <ThemeProvider>
          <ApolloProvider client={client}>
            <ExperimentProvider
              fallback={<BowtieDialogSpinner open />}
              initializeUser={initializeExperimentUser}
              client={statsigClient}
            >
              <AuthProvider onUserChange={onUserChange}>
                <UserAuthProvider>
                  <QuotationProvider>
                    <Header />
                    <Notifier />
                    <QuotationWidget />
                  </QuotationProvider>
                </UserAuthProvider>
              </AuthProvider>
            </ExperimentProvider>
          </ApolloProvider>
        </ThemeProvider>
      </LocaleProvider>
    </AttributeAndQsContextProvider>
  </Router>
);

export default App;
